"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Loader } from "lucide-react";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light/50 border border-primary/20 rounded-lg">
        <p className="text-primary-dark text-lg font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-medium">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" className="transition-transform duration-200 focus:scale-[1.02] bg-white border-muted-foreground/20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-medium">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" className="transition-transform duration-200 focus:scale-[1.02] bg-white border-muted-foreground/20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-medium">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" className="transition-transform duration-200 focus:scale-[1.02] bg-white border-muted-foreground/20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="property_address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-medium">Property Address</FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, City, State ZIP" className="transition-transform duration-200 focus:scale-[1.02] bg-white border-muted-foreground/20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <FormControl>
                <input type="checkbox" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} className="mt-1 size-4 rounded border-muted-foreground/30 text-foreground focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all cursor-pointer" />
              </FormControl>
              <FormLabel className="text-sm text-foreground-light font-normal leading-relaxed cursor-pointer">
                I Consent to receive marketing text messages from Cash Home at the number provided. Message frequency varies. Message & data rates may apply. Text HELP for assistance. Reply STOP to opt out.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="nonMarketingConsent" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <FormControl>
                <input type="checkbox" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} className="mt-1 size-4 rounded border-muted-foreground/30 text-foreground focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all cursor-pointer" />
              </FormControl>
              <FormLabel className="text-sm text-foreground-light font-normal leading-relaxed cursor-pointer">
                I Consent to Receive non-marketing text messages from Cash Home about your property offer. Message frequency varies. Message & data rates may apply. Text HELP for assistance. Reply STOP to opt out.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full relative overflow-hidden transition-all duration-200 hover:scale-[1.03] active:scale-100">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            <span>Submitting...</span>
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_1.5s_infinite]" />
          </> : 'Get My Cash Offer'}
      </Button>

      <div className="text-center mt-4 text-xs" style={{
      color: '#666666'
    }}>
        <Link to="https://needed-slug.10web.cloud/privacy-policy" className="underline hover:opacity-80 transition-opacity">
          Privacy Policy
        </Link>
        <span className="mx-1">|</span>
        <Link to="https://needed-slug.10web.cloud/terms-of-service" className="underline hover:opacity-80 transition-opacity">
          Terms of Service
        </Link>
      </div>
    </Form>;
}
export default function ContactQuote() {
  return <section id="contact" className="py-16 px-4 bg-background scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-foreground-light leading-tight">
              Ready to Sell Your House for Cash?
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Get a fast, no-obligation cash offer today. We cover all fees including title, closing, and transfer costs. Fill out the form and receive your offer within 24 hours.
            </p>
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <svg className="size-5 text-foreground" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">No Hidden Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="size-5 text-foreground" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Fast Closing</span>
              </div>
            </div>
          </div>

          <FormProvider className="bg-muted/30 p-8 rounded-lg shadow-soft border border-muted/40" formId="contact-form-quote" sectionName="contact-quote" defaultValues={{
          name: "",
          email: "",
          phone: "",
          property_address: "",
          consent: false,
          nonMarketingConsent: false
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            minLength: 10,
            maxLength: 15
          },
          property_address: {
            required: true,
            minLength: 5,
            maxLength: 200
          },
          consent: {
            required: true,
            custom: value => value === true ? null : "Please agree to receive SMS messages to continue"
          },
          nonMarketingConsent: {
            required: true,
            custom: value => value === true ? null : "Please agree to receive non-marketing SMS messages to continue"
          }
        }} submitText="Get My Cash Offer" successMessage="Thank you! We'll contact you within 24 hours with your cash offer." errorMessage="Unable to submit your request. Please try again or call us at (800) 555-1234.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </section>;
}